<template>
    <div class="relevant-artists-wrapper">
        <p v-if="relevantArtistsList" class="relevant-artists-title">Search Results</p>
        <div v-if="relevantArtistsList">
            <ReviewOffersPage
                v-for="(artistInfo, index) in relevantArtists"
                :key="index"
                :artistInfo="artistInfo"
                :mode="mode"
            />
        </div>
        <div v-if="!relevantArtistsList" class="search-result-container">
            <div
                id="preloader"
                class="preloader"
                :class="{ 'd-none': load, 'd-block': !load }"
            ></div>
            <div class="search-title">
                <p class="search-filter-title">Search Results</p>
                <hr style="background: rgb(100, 100, 100); width: 100%" />
            </div>
            <div class="search-filter">
                <div class="search">
                    <div class="sound">
                        <p class="title">Sound</p>
                        <div class="input-wrapper">
                            <label for="vibe">Filter by Vibe</label>
                            <input
                                v-model="$v.search.vibe.$model"
                                type="text"
                                class="vibe-input"
                                placeholder="Add Vibe"
                                @keyup.enter="startSearchVibe"
                            />
                            <p
                                v-if="$v.search.vibe.$dirty && !$v.search.vibe.correctFormat"
                                class="error-msg"
                            >
                                Please enter comma separated keywords.
                            </p>
                        </div>
                    </div>
                    <div class="where">
                        <p class="title">Where</p>
                        <div class="location-input-wrapper">
                            <div class="address">
                                <label for="location">Filter by Location</label>
                                <input
                                    ref="autocomplete"
                                    v-model="$v.search.location.$model"
                                    class="location-input"
                                    type="text"
                                    placeholder="Add Location"
                                />
                                <input type="hidden" v-model="$v.search.lat.$model" />
                                <input type="hidden" v-model="$v.search.lon.$model" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="!paginatedArtists.length && load" class="search-result-empty">
                <p>Your search got no hits. Try removing some of the filters.</p>
            </div>
            <div v-if="paginatedArtists.length && load" class="search-container">
                <Artist
                    v-for="(artist, indx) in paginatedArtists"
                    :key="`${artist.id}-${indx}`"
                    :artist="artist"
                    :cta="'View Artist'"
                    @book-artist="bookArtist"
                />
                <Pagination
                    :currentPage="currentPage"
                    :totalPages="totalPages"
                    :visiblePagesCount="getLengthArtists"
                    @page-change="handlePageChange"
                />
            </div>
        </div>
    </div>
</template>

<script>
    import axios from 'axios'
    import { notification } from '@/helpers/notification'
    import { useAuth } from '@/composables/auth/useAuth'
    import ReviewOffersPage from './booking-part/booked-artist/ReviewOffersPage.vue'
    import Artist from '../search-result-artist/Artist.vue'
    import Pagination from '../search-result-artist/Pagination.vue'

    const URLS = {
        redirect: {
            booking: '/booking-manager',
            artist: '/artists',
            search: '/search?'
        },
        xhr: {
            fetch: {
                checkAuth: '/api/check-authorization',
                user: '/api/profile/v1/settings/user'
            }
        }
    }

    export default {
        name: 'RelevantArtistsList',
        components: {
            ReviewOffersPage,
            Artist,
            Pagination
        },
        emits: ['update:validation'],
        validations: {
            search: {
                vibe: {
                    correctFormat: function (value) {
                        return (
                            !value || /^[&0-9a-zA-Z+\-_ ]+(,[&0-9a-zA-Z+\-_ ]+)*$/.test(value)
                        )
                    }
                },
                location: {},
                lat: {},
                lon: {}
            }
        },
        watch: {
            $v: {
                handler: function (validation) {
                    this.$emit('update:validation', validation)
                },
                immediate: true
            },
            '$v.search.location.$model': function (location) {
                if (location) {
                    this.$v.search.lat.$touch()
                    this.$v.search.lon.$touch()
                }
            }
        },
        mounted() {
            this.$nextTick(() => {
                if (this.$refs?.autocomplete) {
                    this.$emit('autocomplete-loaded', this.$refs.autocomplete)
                }
            })
            setTimeout(() => {
                this.initPlaceApi()
            }, 700)
        },
        setup() {
            const { showAuthModal } = useAuth()
            return {
                showAuthModal
            }
        },
        data: () => ({
            authed: false,
            userInfo: {},
            searchParams: {},
            defaultSearchParams: {
                location: '',
                city: '',
                lat: '',
                lon: '',
                search: '',
                date: ''
            },
            currentLocation: '',
            currentPage: 1,
            records: [],
            perPage: 10,
            relevantArtists: [],
            filterResult: [],
            relevantArtistsList: null,
            mode: 'searchResults',
            load: false,
            contactId: null,
            search: {},
            validation: {
                required: true
            }
        }),
        methods: {
            startSearchVibe(e) {
                this.populateSearchParams(e.srcElement.value, null, null, null)
                this.search.vibe = e.srcElement.value
                this.getArtistsList()
            },
            initPlaceApi() {
                let placeElement = this.$refs.autocomplete
                if (placeElement) {
                    let autocomplete = new google.maps.places.Autocomplete(placeElement, {
                        types: ['(regions)'],
                        componentRestrictions: { country: 'us' }
                    })
                    autocomplete.addListener('place_changed', () => {
                        let place = autocomplete.getPlace()
                        let latitude = place.geometry.location.lat()
                        let longitude = place.geometry.location.lng()
                        let location = place.formatted_address
                        this.populateSearchParams(null, location, latitude, longitude)
                        this.search.location = location
                        this.getArtistsList()
                    })
                }
            },
            bookArtist(artistInfo) {
                if (this.authed) {
                    if (this.userInfo.bookingVenue) {
                        localStorage.setItem('artistEmail', artistInfo.band_email)
                        localStorage.setItem('artistName', artistInfo.band_name)
                        this.redirectToBookingFlow()
                    } else {
                        this.redirectToArtist(artistInfo)
                    }
                } else {
                    this.showAuthModal = true
                }
            },
            redirectToBookingFlow() {
                window.open(`${URLS.redirect.booking}/#referred-from-artists-page`, '_blank')
            },
            redirectToArtist(artist) {
                window.open(`${URLS.redirect.artist}/${artist.slug}`, '_blank')
            },
            async getRelevantArtistsList() {
                try {
                    const response = await axios.post(
                        '/booking-manager/relevant-artists-list',
                        {
                            relevantArtists: this.relevantArtistsList
                        }
                    )
                    this.relevantArtists = response.data.data
                } catch (error) {
                    notification('error', error.response.data.errors.message)
                }
            },
            async getArtistsList() {
                try {
                    const response = await axios.get('/api/search/artists', {
                        params: this.searchParams
                    })
                    this.filterResult = response.data.data.result
                        .map((user) => ({
                            ...user,
                            artists: user.artists
                        }))
                        .flatMap((user) =>
                            user.artists.map((artist) => ({
                                ...artist,
                                user
                            }))
                        )

                    this.currentLocation = response.data.data.location.location
                    localStorage.setItem('currentLocation', this.currentLocation)
                    this.load = true
                } catch (error) {
                    notification('error', error.response.data.errors.message)
                }
            },
            populateSearchParams(
                vibe = null,
                location = null,
                latitude = null,
                longitude = null
            ) {
                let params = new URL(window.location).searchParams

                this.searchParams = {
                    location:
                        location ??
                        params.get('location') ??
                        this.defaultSearchParams.location,
                    city: params.get('city') ?? this.defaultSearchParams.city,
                    lat: latitude ?? params.get('lat') ?? this.defaultSearchParams.lat,
                    lon: longitude ?? params.get('lon') ?? this.defaultSearchParams.lon,
                    search: vibe ?? params.get('search') ?? this.defaultSearchParams.search,
                    date: params.get('date') ?? this.defaultSearchParams.date
                }
            },
            handlePageChange(page) {
                this.currentPage = page
            },
            async fetchUserInfo() {
                const response = await axios.get(URLS.xhr.fetch.user)

                return response.data.data[0]
            },
            async checkAuthorization() {
                try {
                    const resp = await axios.get(URLS.xhr.fetch.checkAuth)

                    if (resp?.data?.isAuthorized) {
                        this.authed = true
                    }
                } catch {
                    this.authed = false
                }
            }
        },
        computed: {
            totalPages() {
                return Math.ceil(this.filterResult.length / this.perPage)
            },
            paginatedArtists() {
                const startIndex = (this.currentPage - 1) * this.perPage
                const endIndex = startIndex + this.perPage

                return this.filterResult.slice(startIndex, endIndex)
            },
            getLengthArtists() {
                return this.filterResult.length
            }
        },
        async created() {
            this.populateSearchParams()
            this.relevantArtistsList = JSON.parse(localStorage.getItem('relevantArtistsList'))
            this.relevantArtistsList =
                /* TODO: analyze if getRelevantArtistList is still necessary */
                await this.getArtistsList() /* ? await this.getRelevantArtistsList() : await this.getArtistsList() */
            await this.checkAuthorization()
            if (this.authed) {
                this.userInfo = await this.fetchUserInfo()
            }
        },
        beforeDestroy() {
            localStorage.removeItem('relevantArtistsList')
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../../sass/_variables.scss';
    .pagination {
        justify-content: space-between;
    }
    .search-result-empty {
        width: 75%;
    }
    .search-result-empty p {
        color: $lighter-grey;
        @include base-font-26;
        line-height: 120%;
        letter-spacing: -0.52px;
    }
    .search-container {
        @include flex-row;
        align-items: flex-start;
        gap: 20px;
        flex-wrap: wrap;
        width: 100%;
    }
    .search-title {
        @include flex-start;
        flex-direction: column;
        width: 100%;
    }
    .search-filter-title {
        color: $white;
        @include base-font-44;
        line-height: 110%;
        letter-spacing: -1.76px;
    }
    .relevant-artists-wrapper {
        @include flex-col;
        align-items: center;
        gap: 64px;
        background: $black;
        padding-bottom: 65px;
        min-height: 100vh;
    }
    .relevant-artists-title {
        color: $white;
        @include base-font-44;
        line-height: 110%;
        letter-spacing: -1.76px;
        text-align: center;
        margin-top: 64px;
    }
    .search-result-container {
        @include flex-col;
        align-items: center;
        gap: 20px;
        width: 100%;
        max-width: 1050px;
    }
    @media (max-width: 600px) {
        .search-container {
            padding: 16px;
            justify-content: center;
        }
        .search-title {
            width: 100%;
            padding: 0 16px;
        }
    }
    .search-filter {
        width: 100%;

        .search {
            display: flex;
            width: 100%;
            flex-direction: row;
            margin-bottom: 20px;
            margin-left: 0px;

            p {
                margin-bottom: 0px;
            }
            button {
                border: none;
            }

            .sound,
            .where {
                @include flex-start;
                width: 100%;
                flex-direction: column;
                justify-content: flex-start;
                gap: 8px;

                .title {
                    color: $lighter-grey;
                    @include base-font-20;
                    line-height: 120%;
                    letter-spacing: -0.2px;
                }

                .location-input-wrapper {
                    @include flex-start;
                    width: 100%;
                    flex-direction: row;
                    justify-content: flex-start;
                    gap: 8px;
                }
            }
            .input-wrapper,
            .address,
            .radius {
                @include flex-start;
                width: 100%;
                flex-direction: column;
                justify-content: flex-start;
            }
            label {
                padding: 4px 2px;
                color: $darker-grey;
                @include base-font-12;
                line-height: 135%;
                letter-spacing: 0.12px;
            }
            input {
                padding: 12px 16px 16px 16px;
                border-radius: 12px;
                border: 1px solid $darker-grey;
                background: $dark;
                overflow: hidden;
                color: $white;
                text-overflow: ellipsis;
                white-space: nowrap;
                @include base-font-16;
                font-weight: 550;
                line-height: 125%;
                align-self: stretch;
                width: 100%;
                -moz-appearance: textfield;
                appearance: textfield;
            }
            input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }
            input::placeholder {
                overflow: hidden;
                color: $grey;
                text-overflow: ellipsis;
                @include base-font-16;
                line-height: 125%;
            }
            .address,
            .input-wrapper {
                width: 100%;
                max-width: 290px;
            }
            .radius {
                width: 100%;
                max-width: 80px;
            }
            .error-msg {
                color: $error;
                @include base-font-8;
                font-weight: 600;
                line-height: 130%;
                letter-spacing: 0.14px;
                text-wrap: nowrap;
            }
        }
    }
</style>
